/**
 * color-palette.ts
 *
 * Paleta de colores definidos por el sistema para selección de usuario.
 * Cada color incluye su valor oklch, lightness extraída y nombre legible.
 *
 * Usos: etiquetas, estados, grupos de tareas, prioridades,
 *       avatares (usuario, proyecto, organización), gráficas.
 *
 * Los colores NO cambian entre light y dark mode — son fijos.
 * El contraste y sutiles se calculan dinámicamente via:
 *   - getTextColor()        → color-contrast.ts
 *   - getSubtleBackground() → color-subtle.ts
 */

export interface PaletteColor {
  /** Nombre del token CSS (sin --) */
  token: string;
  /** Nombre legible para UI */
  label: string;
  /** Valor oklch completo */
  value: string;
  /** Lightness extraída para cálculos de contraste */
  l: number;
  /** Grupo de color para organización en el selector */
  group: PaletteGroup;
}

export type PaletteGroup =
  | 'red'
  | 'coral'
  | 'orange'
  | 'yellow'
  | 'lime'
  | 'green'
  | 'teal'
  | 'cyan'
  | 'indigo'
  | 'purple'
  | 'magenta'
  | 'pink'
  | 'gray';

export const PALETTE: PaletteColor[] = [
  // ── Red ─────────────────────────────────────────────────────
  { token: 'red-ember', label: 'Ember', value: 'oklch(0.68 0.21 25)', l: 0.68, group: 'red' },
  { token: 'red-scarlet', label: 'Scarlet', value: 'oklch(0.70 0.22 20)', l: 0.7, group: 'red' },
  { token: 'red-crimson', label: 'Crimson', value: 'oklch(0.67 0.20 15)', l: 0.67, group: 'red' },
  { token: 'red-rosewood', label: 'Rosewood', value: 'oklch(0.69 0.19 10)', l: 0.69, group: 'red' },

  // ── Coral ────────────────────────────────────────────────────
  { token: 'coral-flame', label: 'Flame', value: 'oklch(0.72 0.20 35)', l: 0.72, group: 'coral' },
  { token: 'coral-sunset', label: 'Sunset', value: 'oklch(0.71 0.21 30)', l: 0.71, group: 'coral' },
  { token: 'coral-salmon', label: 'Salmon', value: 'oklch(0.70 0.19 25)', l: 0.7, group: 'coral' },
  {
    token: 'coral-emberlight',
    label: 'Ember light',
    value: 'oklch(0.69 0.20 32)',
    l: 0.69,
    group: 'coral',
  },

  // ── Orange ───────────────────────────────────────────────────
  { token: 'orange-blaze', label: 'Blaze', value: 'oklch(0.74 0.20 50)', l: 0.74, group: 'orange' },
  {
    token: 'orange-tangerine',
    label: 'Tangerine',
    value: 'oklch(0.73 0.21 45)',
    l: 0.73,
    group: 'orange',
  },
  {
    token: 'orange-apricot',
    label: 'Apricot',
    value: 'oklch(0.72 0.18 40)',
    l: 0.72,
    group: 'orange',
  },
  {
    token: 'orange-amberglow',
    label: 'Amber glow',
    value: 'oklch(0.71 0.19 55)',
    l: 0.71,
    group: 'orange',
  },

  // ── Yellow ───────────────────────────────────────────────────
  { token: 'yellow-solar', label: 'Solar', value: 'oklch(0.80 0.18 95)', l: 0.8, group: 'yellow' },
  { token: 'yellow-gold', label: 'Gold', value: 'oklch(0.78 0.17 90)', l: 0.78, group: 'yellow' },
  { token: 'yellow-honey', label: 'Honey', value: 'oklch(0.77 0.16 85)', l: 0.77, group: 'yellow' },
  {
    token: 'yellow-sunbeam',
    label: 'Sunbeam',
    value: 'oklch(0.79 0.17 100)',
    l: 0.79,
    group: 'yellow',
  },

  // ── Lime ─────────────────────────────────────────────────────
  { token: 'lime-zest', label: 'Zest', value: 'oklch(0.78 0.20 120)', l: 0.78, group: 'lime' },
  { token: 'lime-spark', label: 'Spark', value: 'oklch(0.76 0.19 115)', l: 0.76, group: 'lime' },
  {
    token: 'lime-electric',
    label: 'Electric',
    value: 'oklch(0.77 0.21 110)',
    l: 0.77,
    group: 'lime',
  },
  { token: 'lime-citrus', label: 'Citrus', value: 'oklch(0.75 0.18 125)', l: 0.75, group: 'lime' },

  // ── Green ────────────────────────────────────────────────────
  {
    token: 'green-emerald',
    label: 'Emerald',
    value: 'oklch(0.70 0.20 145)',
    l: 0.7,
    group: 'green',
  },
  {
    token: 'green-spring',
    label: 'Spring',
    value: 'oklch(0.72 0.18 140)',
    l: 0.72,
    group: 'green',
  },
  { token: 'green-leaf', label: 'Leaf', value: 'oklch(0.69 0.19 150)', l: 0.69, group: 'green' },
  { token: 'green-jade', label: 'Jade', value: 'oklch(0.71 0.20 155)', l: 0.71, group: 'green' },

  // ── Teal ─────────────────────────────────────────────────────
  { token: 'teal-lagoon', label: 'Lagoon', value: 'oklch(0.70 0.19 170)', l: 0.7, group: 'teal' },
  { token: 'teal-oasis', label: 'Oasis', value: 'oklch(0.69 0.18 175)', l: 0.69, group: 'teal' },
  { token: 'teal-aqua', label: 'Aqua', value: 'oklch(0.71 0.20 165)', l: 0.71, group: 'teal' },
  { token: 'teal-wave', label: 'Wave', value: 'oklch(0.72 0.19 180)', l: 0.72, group: 'teal' },

  // ── Cyan ─────────────────────────────────────────────────────
  {
    token: 'cyan-glacier',
    label: 'Glacier',
    value: 'oklch(0.71 0.18 200)',
    l: 0.71,
    group: 'cyan',
  },
  { token: 'cyan-frost', label: 'Frost', value: 'oklch(0.70 0.19 205)', l: 0.7, group: 'cyan' },
  { token: 'cyan-arctic', label: 'Arctic', value: 'oklch(0.72 0.17 195)', l: 0.72, group: 'cyan' },
  { token: 'cyan-polar', label: 'Polar', value: 'oklch(0.69 0.18 210)', l: 0.69, group: 'cyan' },

  // ── Indigo ───────────────────────────────────────────────────
  {
    token: 'indigo-nebula',
    label: 'Nebula',
    value: 'oklch(0.68 0.19 270)',
    l: 0.68,
    group: 'indigo',
  },
  {
    token: 'indigo-vortex',
    label: 'Vortex',
    value: 'oklch(0.69 0.20 275)',
    l: 0.69,
    group: 'indigo',
  },
  {
    token: 'indigo-cosmos',
    label: 'Cosmos',
    value: 'oklch(0.67 0.18 280)',
    l: 0.67,
    group: 'indigo',
  },
  { token: 'indigo-deep', label: 'Deep', value: 'oklch(0.70 0.17 265)', l: 0.7, group: 'indigo' },

  // ── Purple ───────────────────────────────────────────────────
  {
    token: 'purple-orchid',
    label: 'Orchid',
    value: 'oklch(0.69 0.20 300)',
    l: 0.69,
    group: 'purple',
  },
  {
    token: 'purple-violet',
    label: 'Violet',
    value: 'oklch(0.68 0.21 295)',
    l: 0.68,
    group: 'purple',
  },
  {
    token: 'purple-amethyst',
    label: 'Amethyst',
    value: 'oklch(0.70 0.19 305)',
    l: 0.7,
    group: 'purple',
  },
  { token: 'purple-iris', label: 'Iris', value: 'oklch(0.71 0.18 290)', l: 0.71, group: 'purple' },

  // ── Magenta ──────────────────────────────────────────────────
  {
    token: 'magenta-fusion',
    label: 'Fusion',
    value: 'oklch(0.70 0.21 325)',
    l: 0.7,
    group: 'magenta',
  },
  {
    token: 'magenta-neon',
    label: 'Neon',
    value: 'oklch(0.69 0.22 330)',
    l: 0.69,
    group: 'magenta',
  },
  {
    token: 'magenta-berry',
    label: 'Berry',
    value: 'oklch(0.68 0.20 335)',
    l: 0.68,
    group: 'magenta',
  },
  {
    token: 'magenta-pulse',
    label: 'Pulse',
    value: 'oklch(0.71 0.19 320)',
    l: 0.71,
    group: 'magenta',
  },

  // ── Pink ─────────────────────────────────────────────────────
  { token: 'pink-bloom', label: 'Bloom', value: 'oklch(0.73 0.18 350)', l: 0.73, group: 'pink' },
  {
    token: 'pink-flamingo',
    label: 'Flamingo',
    value: 'oklch(0.72 0.19 345)',
    l: 0.72,
    group: 'pink',
  },
  { token: 'pink-petal', label: 'Petal', value: 'oklch(0.74 0.17 355)', l: 0.74, group: 'pink' },
  { token: 'pink-candy', label: 'Candy', value: 'oklch(0.71 0.18 340)', l: 0.71, group: 'pink' },

  // ── Gray ─────────────────────────────────────────────────────
  { token: 'gray-silver', label: 'Silver', value: 'oklch(0.87 0.00 0)', l: 0.87, group: 'gray' },
  { token: 'gray-stone', label: 'Stone', value: 'oklch(0.78 0.00 0)', l: 0.78, group: 'gray' },
  { token: 'gray-graphite', label: 'Graphite', value: 'oklch(0.60 0.00 0)', l: 0.6, group: 'gray' },
  { token: 'gray-iron', label: 'Iron', value: 'oklch(0.50 0.00 0)', l: 0.5, group: 'gray' },
];

/**
 * Paleta agrupada — útil para renderizar el selector de color
 * organizado por familias de color.
 */
export const PALETTE_BY_GROUP = PALETTE.reduce<Record<PaletteGroup, PaletteColor[]>>(
  (acc, color) => {
    acc[color.group].push(color);
    return acc;
  },
  {
    red: [],
    coral: [],
    orange: [],
    yellow: [],
    lime: [],
    green: [],
    teal: [],
    cyan: [],
    indigo: [],
    purple: [],
    magenta: [],
    pink: [],
    gray: [],
  },
);

/**
 * Busca un color de la paleta por su token.
 *
 * @param token - Nombre del token (ej: "red-ember")
 * @returns PaletteColor o undefined si no existe
 *
 * @example
 * getPaletteColor("red-ember")?.value // → "oklch(0.68 0.21 25)"
 */
export function getPaletteColor(token: string): PaletteColor | undefined {
  return PALETTE.find((c) => c.token === token);
}

/**
 * Resolves a stored color to a CSS-renderable value.
 *
 * @remarks
 * A palette token (e.g. `"red-ember"`) resolves to its `oklch()` value. Anything else —
 * a raw CSS color/hex stored before the token system existed — passes through unchanged,
 * so legacy values keep rendering instead of silently disappearing. `null`/`undefined`/empty
 * input returns `undefined`.
 */
export function resolveColorValue(token: string | null | undefined): string | undefined {
  if (!token) return undefined;
  return getPaletteColor(token)?.value ?? token;
}

/**
 * Colores que requieren texto oscuro (L >= 0.76).
 * Exportado como Set para lookups O(1).
 */
export const DARK_TEXT_TOKENS = new Set(PALETTE.filter((c) => c.l >= 0.76).map((c) => c.token));
